/**
 * @jsxRuntime classic
 * @jsx jsx
 *
 * PostStreamReviewScreen — floating toolbar shown after an AI session ends.
 *
 * Reuses the AI modal infrastructure (decoration plugin + ModalRegion) to render
 * a post-stream review toolbar with the following affordances:
 *   - Undo / Redo (operate on stored slices, NOT the editor history plugin)
 *   - View changes (toggle a diff overlay for the current segment)
 *   - Ask Rovo (a small prompt input)
 *   - Step through changes (reveals Previous / Next navigation between AI change segments)
 *
 * Only mounted when `useUIState` returns `'postStreamReview'`, which requires
 * both `platform_editor_ai_xstate_migration` and
 * `platform_editor_ai_new_aifc_editor_experience` gates to be ON.
 *
 * File layout (this directory):
 *   - `index.tsx` (this file) — screen component, plugin-state wiring, action handlers
 *   - `AskRovo.tsx` — prompt input pill with inline step-through navigation,
 *     show/hide changes toggle and Undo/Redo controls
 *   - `hooks.ts` — `useOutsideClickClose`, `useHideDiffOnUnmount`, `useSegmentNavigation`
 *   - `icons.tsx` — hoisted icon render functions (stable refs)
 *   - `messages.ts` — i18n message descriptors
 *
 * Note: CSS style objects (`wrapperCSS`, `toolbarCSS`) live inline in this
 * file rather than a separate `styles.ts` — `@compiled/react`'s build-time
 * transform requires `css({})` calls to be co-located with the JSX that
 * consumes them, so a cross-file styles module is not supported.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { EditorAppearance, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import type { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';

import type { AIPlugin } from '../../../../../aiPluginType';
import { openPostStreamReviewModal } from '../../../../../editor-actions/actions';
import {
	resetSessionState,
	setStreamReviewSegments,
	startFreeGenStream,
} from '../../../../../pm-plugins/ai-session-context/commands';
import type { AIContentPositionEntry } from '../../../../../pm-plugins/ai-session-context/types';
import { rovoAgentsPluginKey } from '../../../../../pm-plugins/rovo-agents/plugin-key';
import { selectionPluginKey } from '../../../../../pm-plugins/selection/selection-plugin-key';
import { useContentComponentContext } from '../../../../hooks/useContentComponentContext';
import { ModalRegionErrorBoundary } from '../../../ExperienceApplicationErrorBoundary/ExperienceApplicationErrorBoundary';
import { ModalRegion } from '../../../ModalRegion/ModalRegion';
import type { Action } from '../../hooks/useUIState';
import { calculateDiffSegments } from '../calculateDiffSegments';
import { calculateTopLevelNodeSegments } from '../calculateTopLevelNodeSegments';
import {
	buildDiffParamsForAllSegments,
	buildDiffParamsForSegment,
	mergeOverlappingSegments,
} from '../diff-helpers';

import { AskRovo } from './AskRovo';
import { useHideDiffOnUnmount, useOutsideClickClose, useSegmentNavigation } from './hooks';
/**
 * `display: contents` — the wrapper exists purely to hold the ref used by the
 * outside-click handler and the screen testId, without introducing a box that
 * affects layout. All visible chrome is on `toolbarCSS` further inside.
 */
const wrapperCSS = css({ display: 'contents' });

/**
 * Compact single-row toolbar — see file-level JSDoc for the layout sketch.
 *
 * Width is composed via DS spacing tokens to keep the value tied to the
 * design-system rhythm rather than a raw pixel literal. The `space.*` scale
 * tops out at `space.1000` (80px), so 672px = 8 × space.1000 + space.400.
 */
const toolbarCSS = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	width: `calc(8 * ${token('space.1000')} + ${token('space.400')})`,
	backgroundColor: token('elevation.surface.overlay'),
	borderRadius: token('radius.xxlarge', '16px'),
	boxShadow: token('elevation.shadow.overlay'),
	boxSizing: 'border-box',
});

/**
 * Selects the first usable decoration to anchor the modal to. Mirrors the helper
 * in `Prompt.tsx` so the modal positioning logic stays consistent across screens.
 */
function findModalDecoration(modalDecorationSet?: DecorationSet): Decoration | undefined {
	if (!modalDecorationSet) {
		return undefined;
	}
	return (
		modalDecorationSet
			.find(
				undefined,
				undefined,
				(spec) =>
					spec.key === 'modalMountPointDecoration' || spec.key === 'modalMountPointRangeDecoration',
			)
			.shift() ||
		modalDecorationSet
			.find(undefined, undefined, (spec) => spec.key === 'ai-nodeDecoration')
			.shift()
	);
}

/**
 * PostStreamReviewScreen — see file-level JSDoc for the full feature description.
 */
export function PostStreamReviewScreen({
	api,
	dispatch,
	appearance,
}: {
	api?: ExtractInjectionAPI<AIPlugin>;
	appearance: EditorAppearance;
	dispatch: React.Dispatch<Action>;
}): JSX.Element | null {
	const {
		editorView,
		wrapperElement,
		endExperience,
		editorPluginAIProvider,
		isInDocumentStreamingEnabled,
	} = useContentComponentContext();

	const {
		lastTriggeredFrom,
		modalMountedTimeStamp,
		modalDecorationSet,
		aiContentPositions,
		streamReviewSegments,
		currentStreamReviewSegmentIndex,
		configItem,
	} = useSharedPluginStateWithSelector(api, ['aiExperience'], ({ aiExperienceState }) => ({
		lastTriggeredFrom: aiExperienceState?.aiDecorationExperiencePluginState.lastTriggeredFrom,
		modalMountedTimeStamp:
			aiExperienceState?.aiDecorationExperiencePluginState.modalMountedTimeStamp,
		modalDecorationSet: aiExperienceState?.aiDecorationExperiencePluginState.modalDecorationSet,
		aiContentPositions: aiExperienceState?.aiSessionPluginState?.aiContentPositions,
		streamReviewSegments: aiExperienceState?.aiSessionPluginState?.streamReviewSegments,
		currentStreamReviewSegmentIndex:
			aiExperienceState?.aiSessionPluginState?.currentStreamReviewSegmentIndex,
		configItem: aiExperienceState?.aiDecorationExperiencePluginState.configItem,
	}));

	// Fallback free-gen prompt input (used when product has no Rovo onSubmit).
	const [textInput, setTextInput] = useState('');
	const textInputRef = useRef(textInput);
	textInputRef.current = textInput;

	// `cancel` is wired into `RovoPromptScreenWithLogic` / `PromptScreen`
	// which can invoke it on benign events (Esc inside the prompt input, click
	// outside the prompt input but still inside the toolbar, etc.). For the
	// Post Stream Review screen we explicitly DO NOT want any of those to tear
	// down the modal — the user is still actively reviewing AI changes and the
	// toolbar must stay anchored until they take an explicit dismiss action.
	const cancel = useCallback(
		(_options?: {
			shouldClearSelectionContext?: boolean;
			shouldIgnoreNextSpaceTrigger?: boolean;
		}) => {
			// no-op — see comment above
		},
		[],
	);

	// AI content range exposed to Rovo as selection context on rework submit.
	// Populated below (needs the segments/mode) and read via a ref so the earlier
	// submit callbacks can reference it without a declaration-order problem.
	const reworkContextRangeRef = useRef<{ endPos: number; startPos: number } | null>(null);

	// Set the Rovo selection context for a rework prompt without installing an
	// editor selection (which would show the floating toolbar). A synthetic
	// `TextSelection` lets the AI selection plugin derive the `SelectionContext`
	// for `getAISelectionContext()` / `ReadContentCommandHandler`; we only
	// `setMeta`, never `setSelection`, so the editor selection is unchanged.
	const setSelectionContextForRework = useCallback(() => {
		if (!editorView || editorView.isDestroyed) {
			return;
		}
		const range = reworkContextRangeRef.current;
		if (!range) {
			return;
		}
		const { state } = editorView;
		const clampedFrom = Math.min(range.startPos, state.doc.content.size);
		const clampedTo = Math.min(range.endPos, state.doc.content.size);
		if (clampedFrom >= clampedTo) {
			return;
		}
		const selection = TextSelection.create(state.doc, clampedFrom, clampedTo);
		const tr = state.tr.setMeta(selectionPluginKey, {
			action: 'lastSelectionContext',
			selection,
		});
		editorView.dispatch(tr);
	}, [editorView]);

	const submit = useCallback(() => {
		const currentInput = textInputRef.current;
		if (!currentInput?.trim()) {
			return;
		}
		// Set the Rovo selection context (no editor selection) before the free-gen
		// rework so the request carries the AI content range.
		setSelectionContextForRework();
		api?.core?.actions.execute(startFreeGenStream(currentInput));
	}, [api?.core?.actions, setSelectionContextForRework]);

	const onTextChange = useCallback((input: string) => {
		setTextInput(input);
	}, []);

	// Rovo chat publish wiring — identical to PromptSessionScreen because the
	// modal runs the same Rovo / fallback prompt UI underneath.
	const rovoChatAIFeatureContext = configItem?.rovoChatAIFeatureContext;
	const rovoChatInteractionSource = configItem?.rovoChatInteractionSource;
	const publishChatNew = useCallback(
		(prompt: DocNode) => {
			const publish = rovoAgentsPluginKey.getState(editorView.state)?.publish;
			if (publish) {
				const payload: Parameters<typeof publish>[0] = {
					type: 'chat-new',
					product: editorPluginAIProvider.product,
					data: {
						dialogues: [],
						prompt,
						...(rovoChatAIFeatureContext ? { aiFeatureContext: rovoChatAIFeatureContext } : {}),
					},
					// Must be a sourceEligible value so
					// getContextRequirements() returns true and getEditorContext()
					// fetches live document context. Mirror the conditional in
					// RovoPromptScreenWithLogic (L221-224).
					source:
						editorPluginAIProvider.product === 'confluence' &&
						(appearance === 'full-page' || appearance === 'full-width' || appearance === 'max')
							? 'CONFLUENCE_INLINE_EDITOR'
							: 'INLINE_EDITOR',
					...(rovoChatInteractionSource ? { interactionSource: rovoChatInteractionSource } : {}),
				};
				publish(payload);
			}
		},
		[
			editorView,
			editorPluginAIProvider.product,
			rovoChatAIFeatureContext,
			rovoChatInteractionSource,
			appearance,
		],
	);

	// Mirror Prompt.tsx — decide Rovo vs fallback prompt presentation.
	const hasRovoPrompt = !!editorPluginAIProvider.onSubmit;
	const { PromptEditor } = editorPluginAIProvider;
	const hasFallbackPrompt = !hasRovoPrompt && !!PromptEditor;

	const screenRef = useRef<HTMLDivElement>(null);
	const segmentIndex = currentStreamReviewSegmentIndex ?? 0;
	// The per-tool-call entries with overlaps merged: when the AI edits the SAME
	// node across tool calls the raw ranges overlap, which corrupts reconstruction
	// (out-of-range steps / wrong undo). Merging ONCE here yields disjoint,
	// doc-tiling changes for every consumer — step-through segmenting and all
	// aggregate operations. Memoized so its identity is stable for the deps below.
	const coarseSegments = useMemo(
		() => mergeOverlappingSegments(aiContentPositions ?? []),
		[aiContentPositions],
	);
	const intl = useIntl();

	const [isStepThroughExpanded, setIsStepThroughExpanded] = useState(false);

	// Two review modes, driven by the step-through toggle (no separate control):
	//   - AGGREGATE (collapsed, default): View changes / Undo / Redo act on ALL the
	//     AI's changes at once, over the coarse `aiContentPositions`.
	//   - STEP-THROUGH (expanded): navigate the working `segments` one at a time.
	const isAggregate = !isStepThroughExpanded;

	// The WORKING segments the STEP-THROUGH mode navigates, one per changed
	// top-level node (or smart change). Live in plugin state so positions stay
	// remapped and per-segment undo/redo survives the undo → remap cycle.
	// (Aggregate mode uses `coarseSegments` directly, not these.)
	const segments = useMemo(() => streamReviewSegments ?? [], [streamReviewSegments]);

	// Compute the working segments. Both strategies start from the top-level-node
	// segments (`calculateTopLevelNodeSegments`, one per changed top-level node):
	//   - gate OFF (`block`) → use those top-level segments as-is.
	//   - gate ON  (`smart`) → refine EACH top-level segment INDEPENDENTLY into
	//     granular (sentence / paragraph / node) sub-segments. Running smart per
	//     segment keeps its `mergeOverlappingByNewDocRange` scoped to a single node,
	//     so distinct top-level nodes are never collapsed into one segment.
	const computeWorkingSegments = useCallback((): AIContentPositionEntry[] => {
		if (!editorView || editorView.isDestroyed || coarseSegments.length === 0) {
			return [];
		}

		const topLevelSegments = calculateTopLevelNodeSegments(coarseSegments);
		if (!fg('platform_editor_ai_diff_based_segmenting')) {
			return topLevelSegments;
		}
		// A top-level segment smart can't sub-divide — a whole-node add/remove, or a
		// change smart doesn't surface — is kept as-is.
		return topLevelSegments.flatMap((segment) => {
			const params = buildDiffParamsForSegment(editorView, segment);
			if (!params || params.steps.length === 0) {
				return [segment];
			}
			const granular = calculateDiffSegments({
				originalDoc: params.originalDoc,
				steps: params.steps,
				diffType: 'smart',
				locale: intl.locale,
			});
			return granular.length > 0 ? granular : [segment];
		});
	}, [editorView, coarseSegments, intl.locale]);

	const computeWorkingSegmentsRef = useRef(computeWorkingSegments);
	// Keep the ref in sync via a effect rather than assigning during render:
	// mutating a ref in the render body is a render-phase side effect that is unsafe
	// under React concurrent mode (renders can be interrupted/replayed).
	useEffect(() => {
		computeWorkingSegmentsRef.current = computeWorkingSegments;
	}, [computeWorkingSegments]);

	// Bootstrap: compute the working segments ONCE when the review opens and
	// publish them to plugin state. Guarded on `segments` being empty so it never
	// re-runs mid-review (which would clobber per-segment undo state) — the plugin
	// keeps positions remapped from there. A fresh AI session clears
	// `streamReviewSegments` (END_SESSION / RESET), so the next open recomputes.
	useEffect(() => {
		if (
			!editorView ||
			editorView.isDestroyed ||
			segments.length > 0 ||
			coarseSegments.length === 0
		) {
			return;
		}
		const computed = computeWorkingSegmentsRef.current();
		if (computed.length > 0) {
			api?.core?.actions.execute(setStreamReviewSegments(computed));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editorView, coarseSegments, segments.length, api]);

	const currentSegment = segments[segmentIndex];
	// Total reviewable changes — drives whether the "Step through changes" toggle
	// is offered (ActionButtons hides it for <= 1) and the "N / total" counter.
	const totalSegments = segments.length;

	// Keep the rework selection-context range in sync with the current view:
	// aggregate = the full AI content span (min/max across coarse segments);
	// step-through = the active segment's range.
	if (isAggregate) {
		reworkContextRangeRef.current = coarseSegments.length
			? {
					startPos: Math.min(...coarseSegments.map((s) => s.startPos)),
					endPos: Math.max(...coarseSegments.map((s) => s.endPos)),
				}
			: null;
	} else {
		reworkContextRangeRef.current = currentSegment
			? { startPos: currentSegment.startPos, endPos: currentSegment.endPos }
			: null;
	}

	// No effect sets the editor selection while the Review moment is open: a real
	// selection would show the floating toolbar and can't represent edits spanning
	// non-contiguous ranges. Instead the diff overlay is always shown (below), and
	// the Rovo selection context is set only on Ask Rovo submit
	// (`setSelectionContextForRework`), so the toolbar stays hidden.

	// Undo/Redo state is **derived** from the stored `isUndone` flags, so the
	// correct button state is restored across Prev/Next navigation.
	//   - Aggregate mode acts on ALL the AI's changes at once, operating on the
	//     coarse `aiContentPositions` (via `coarseSegments`): Undo is enabled while
	//     ANY change is still applied, Redo while ANY change is undone.
	//   - Step-through acts on the single active working segment
	//     (`streamReviewSegments`): Undo when it is applied, Redo when undone.
	const canUndo = isAggregate
		? coarseSegments.some((s) => !s.isUndone)
		: !!currentSegment && !currentSegment.isUndone;
	const canRedo = isAggregate
		? coarseSegments.some((s) => !!s.isUndone)
		: !!currentSegment?.isUndone;

	const onToggleStepThrough = useCallback(() => {
		// Compute the next mode from current state and update it with a
		// plain (non-functional) setState so the state updater stays pure. The
		// side-effect (dispatching the modal command) is performed exactly once
		// here, NOT inside the updater — React may invoke functional updaters more
		// than once (e.g. StrictMode dev double-invoke), which would double-dispatch.
		const next = !isStepThroughExpanded;
		setIsStepThroughExpanded(next);
		if (!editorView || editorView.isDestroyed) {
			return;
		}
		// The working `segments` already live in plugin state (computed on open and
		// shared by both modes), so toggling only re-anchors the modal — no
		// segment (re)computation here.
		if (next) {
			// Entering step-through: start at the first segment.
			openPostStreamReviewModal({
				editorView,
				segmentData: {
					segmentIndex: 0,
					positions: { startPos: segments[0]?.startPos, endPos: segments[0]?.endPos },
				},
			});
		} else {
			// Returning to AGGREGATE mode: re-anchor below the LAST segment and span
			// the highlight across ALL segments. Use `coarseSegments` (what aggregate
			// mode actually operates on) so the span matches the initial aggregate open
			// — fine `segments` can contain zero-width `remove` entries that would
			// narrow the Math.min/max span for structural edits.
			const aggregateStart = coarseSegments.length
				? Math.min(...coarseSegments.map((s) => s.startPos))
				: 0;
			const aggregateEnd = coarseSegments.length
				? Math.max(...coarseSegments.map((s) => s.endPos))
				: 0;
			openPostStreamReviewModal({
				editorView,
				segmentData: {
					segmentIndex: Math.max(0, segments.length - 1),
					positions: { startPos: aggregateStart, endPos: aggregateEnd },
				},
			});
		}
	}, [editorView, segments, coarseSegments, isStepThroughExpanded]);

	const close = useCallback(() => {
		endExperience({ shouldClearSelectionContext: true });
		api?.core?.actions.execute(resetSessionState());
		dispatch({ type: 'terminate' });
	}, [endExperience, api, dispatch]);

	useOutsideClickClose(screenRef, close);

	const onBeforeSubmitForRovoChat = useCallback(
		(prompt: DocNode): boolean => {
			// Set the Rovo selection context (no editor selection) before publishing
			// so the chat picks up the AI content range.
			setSelectionContextForRework();
			publishChatNew(prompt);
			// Use `close()` (not `cancel()`) because `cancel` is intentionally
			// a no-op in this screen — `close()` is the only path that
			// actually tears down the modal.
			close();
			return true;
		},
		[setSelectionContextForRework, publishChatNew, close],
	);

	const openInRovoChatConfig = useMemo(
		() => ({ onBeforeSubmit: onBeforeSubmitForRovoChat }),
		[onBeforeSubmitForRovoChat],
	);

	/**
	 * Apply a slice to the current segment's positions, then re-issue the Post
	 * Stream Review modal so its decoration is freshly anchored at the new
	 * `[newStartPos, newEndPos]` range — all in a SINGLE transaction.
	 *
	 * Why a single transaction:
	 *   - `openPostStreamReviewModal` is driven by `createOpenAIModalCommand`,
	 *     which uses `createCommand` and produces ONE transaction.
	 *   - The slice-replace + `UPDATE_AI_CONTENT_POSITION` meta need to be
	 *     visible to that command's transform callback (so the session-context
	 *     plugin's `apply()` sees the new range and
	 *     `aiContentPositions[segmentIndex]` before the decoration plugin maps
	 *     `modalDecorationSet`).
	 *   - We pass them as `beforeTransform`, which runs at the start of the
	 *     command's transform. That way the slice-replace lands in the same
	 *     `tr` as the `SHOW_MODAL` action — no two-dispatch races, no flicker,
	 *     no risk of `modalDecorationSet` collapsing between dispatches.
	 */
	const replaceCurrentSegmentWith = useCallback(
		(direction: 'undo' | 'redo') => {
			// Aggregate mode applies every coarse change and does NOT depend on
			// `currentSegment` (which may be undefined if `segmentIndex` is stale).
			// Only step-through requires a valid `currentSegment`.
			if (!editorView || (!isAggregate && !currentSegment)) {
				return;
			}

			// Aggregate: apply every applicable coarse change's slice in one
			// transaction, back-to-front (descending startPos) so each replace can't
			// shift the not-yet-processed ranges. Undo applies `originalUserContentSlice`,
			// redo applies `newSlice`; only changes in the opposite state are touched.
			// Uses coarse (not the working segments) because coarse tiles the doc and
			// its originals reconstruct the true pre-edit doc; the working segments do
			// not tile for structural edits (1→N expansion, add adjacent to remove).
			if (isAggregate) {
				const docSize = editorView.state.doc.content.size;
				// Drop changes whose stored range is out of bounds (e.g. a collaborator
				// deleted the content) so they can't throw NOR widen the highlight span.
				const targets = [...coarseSegments]
					.filter(
						(seg) =>
							(direction === 'undo' ? !seg.isUndone : seg.isUndone) &&
							seg.startPos >= 0 &&
							seg.endPos >= seg.startPos &&
							seg.endPos <= docSize,
					)
					.sort((a, b) => b.startPos - a.startPos);

				// Nothing to apply: either every segment is already in the requested
				// state, or all stored ranges are out-of-bounds. Bail out before
				// dispatching the modal — otherwise `aggregateStart`/`aggregateEnd`
				// would fall back to `0` and re-open the modal with a spurious
				// zero-width highlight at the document start.
				if (targets.length === 0) {
					return;
				}

				// Keep the modal anchored below the LAST segment and the highlight
				// spanning the segments after the undo/redo. Span only the segments we
				// actually touch (`targets`) so the highlight never anchors to a range
				// no segment restored. We use the pre-transform positions as the
				// anchor; the decoration plugin maps them through `beforeTransform`.
				const aggregateStart = Math.min(...targets.map((seg) => seg.startPos));
				const aggregateEnd = Math.max(...targets.map((seg) => seg.endPos));

				// Flip every entry's `isUndone` atomically with the slice-replace and
				// modal re-open (single transaction) so `canUndo`/`canRedo` never show a
				// stale state for a frame.
				openPostStreamReviewModal({
					editorView,
					segmentData: {
						segmentIndex,
						positions: { startPos: aggregateStart, endPos: aggregateEnd },
					},
					undoneUpdate: { scope: 'all', isUndone: direction === 'undo' },
					beforeTransform: (tr) => {
						for (const seg of targets) {
							const sliceToApply =
								direction === 'undo' ? seg.originalUserContentSlice : seg.newSlice;
							try {
								tr.replaceRange(seg.startPos, seg.endPos, sliceToApply);
							} catch {
								// Skip a segment whose range is no longer valid.
							}
						}
						return tr;
					},
				});
				return;
			}

			const sliceToApply =
				direction === 'undo' ? currentSegment.originalUserContentSlice : currentSegment.newSlice;
			const { startPos, endPos } = currentSegment;

			// Flip the fine segment's own `isUndone` (not the coarse entry's) atomically
			// with the slice-replace and modal re-open, so Prev/Next restores the correct
			// Undo/Redo state and `canUndo`/`canRedo` never render a stale value.
			openPostStreamReviewModal({
				editorView,
				segmentData: { segmentIndex, positions: { startPos, endPos } },
				undoneUpdate: { scope: 'segment', index: segmentIndex, isUndone: direction === 'undo' },
				beforeTransform: (tr) => {
					try {
						tr.replaceRange(startPos, endPos, sliceToApply);
					} catch {
						// If the range is no longer valid (e.g. collaborator deleted it),
						// fall back to just re-opening the modal at the stored positions
						// — nothing to undo / redo.
					}
					return tr;
				},
			});
		},
		[editorView, currentSegment, segmentIndex, isAggregate, coarseSegments],
	);

	// The diff overlay is always shown while reviewing; `showDeletedChanges` only
	// controls whether DELETED content is rendered: `false` (default) =>
	// `hideDeletedDiffs: true` (clean view); `true` => deletions also shown (via
	// "View changes"). Local state so it resets to the default on Prev/Next.
	const [showDeletedChanges, setShowDeletedChanges] = useState(false);
	// The show/hide-deletions toggle is enabled while any change is still applied
	// (aggregate) / the current segment is applied (per-segment).
	const canViewChanges = canUndo;

	// Pull the showDiff command bag into locals so the `useCallback` deps array
	// can reference the parent objects instead of destructured leaves (ESLint's
	// `exhaustive-deps` treats `api.showDiff.commands.hideDiff` as depending on
	// the whole `api.showDiff.commands` object).
	const showDiffCommands = api?.showDiff?.commands;
	const coreActions = api?.core?.actions;

	// Tear down the diff overlay. Used before an undo/redo: the diff must not be
	// painted while the undo/redo transaction is computed, because for a `remove`
	// segment the removed content is rendered as a diff decoration and leaving it
	// visible makes the undo/redo apply at the wrong position. After the modal
	// re-opens, the always-on diff effect re-shows the overlay.
	const hideDiff = useCallback(() => {
		if (showDiffCommands?.hideDiff && coreActions) {
			coreActions.execute(showDiffCommands.hideDiff);
		}
	}, [showDiffCommands, coreActions]);

	const onUndo = useCallback(() => {
		hideDiff();
		replaceCurrentSegmentWith('undo');
	}, [hideDiff, replaceCurrentSegmentWith]);
	const onRedo = useCallback(() => {
		hideDiff();
		replaceCurrentSegmentWith('redo');
	}, [hideDiff, replaceCurrentSegmentWith]);

	// Non-toggling: render the diff overlay for the current view (aggregate = all
	// segments, per-segment = the current segment). `hideDeleted` controls whether
	// deleted content is suppressed. Safe to call when a diff is already shown —
	// `showDiff` replaces the existing decorations.
	const showCurrentDiff = useCallback(
		(hideDeleted: boolean) => {
			// Aggregate mode diffs ALL coarse changes and does NOT depend on
			// `currentSegment` (which may be undefined if `segmentIndex` is stale).
			// Only step-through requires a valid `currentSegment`.
			if (!editorView || (!isAggregate && !currentSegment) || !showDiffCommands?.showDiff) {
				return;
			}
			// Aggregate diffs ALL changes from coarse; step-through diffs only the
			// current working segment. Aggregate uses coarse (not the working segments)
			// so the reconstructed "before" is the true pre-edit doc — see the undo/redo
			// note above.
			const result = isAggregate
				? buildDiffParamsForAllSegments(editorView, coarseSegments)
				: buildDiffParamsForSegment(editorView, currentSegment);
			if (!result?.steps || result.steps.length === 0 || !result.originalDoc) {
				return;
			}
			coreActions?.execute(
				showDiffCommands.showDiff({
					originalDoc: result.originalDoc,
					steps: result.steps,
					scrollIntoView: true,
					isInverted: false,
					// When `true`, deleted content is suppressed and only added/updated
					// content is highlighted — the default review view. "View changes"
					// flips this to `false` to also reveal deletions.
					hideDeletedDiffs: hideDeleted,
					hideAddedDiffsUnderline: hideDeleted,
					// Render the inline add/remove indicators in the diff
					// overlay. The show-diff plugin internally gates actual rendering
					// behind `platform_editor_diff_plugin_extended`, so passing `true`
					// unconditionally is safe (no-op when that gate is off).
					showIndicators: true,
					// Always request the `smart` overlay. show-diff gates smart rendering
					// behind `platform_editor_ai_smart_diff` and falls back to inline when
					// that gate is off, which is still coherent.
					diffType: 'smart',
				}),
			);
		},
		[editorView, currentSegment, coreActions, showDiffCommands, isAggregate, coarseSegments],
	);

	// "View changes" no longer toggles the diff on/off — the diff is always shown.
	// It toggles whether DELETED content is revealed, re-issuing the diff overlay
	// with the flipped `hideDeletedDiffs`.
	const onViewChanges = useCallback(() => {
		const next = !showDeletedChanges;
		setShowDeletedChanges(next);
		showCurrentDiff(!next);
	}, [showDeletedChanges, showCurrentDiff]);

	// Reset the "show deleted" toggle back to its default whenever the user
	// navigates between segments (Prev/Next) or switches review mode. Each new
	// view starts in the clean `hideDeletedDiffs: true` state.
	//
	// Note: `isStepThroughExpanded` is intentionally NOT reset here. Navigating
	// between segments via the Prev/Next chevrons changes `segmentIndex`, and the
	// step-through tray must stay open while the user steps through changes. It is
	// only toggled explicitly via the step-through button.
	useEffect(() => {
		// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
		setShowDeletedChanges(false);
	}, [segmentIndex, isAggregate]);

	// Always-on diff: re-paint the DEFAULT (deletions hidden) view whenever the
	// view changes — open, Prev/Next (`segmentIndex`), aggregate/step-through
	// toggle (`isAggregate`), or when segments first appear. `showDeletedChanges`
	// is intentionally not a dep (the "View changes" button drives that via
	// `onViewChanges`). `showCurrentDiff` is read via a ref to avoid re-firing on
	// its frequently-changing deps.
	const showCurrentDiffRef = useRef(showCurrentDiff);
	showCurrentDiffRef.current = showCurrentDiff;
	useEffect(() => {
		if (!canViewChanges) {
			return;
		}
		// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
		showCurrentDiffRef.current(true);
		// Re-evaluate whenever the active view changes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [segmentIndex, isAggregate, segments.length, canViewChanges]);

	useHideDiffOnUnmount(coreActions, showDiffCommands?.hideDiff);

	const { goToPrevious, goToNext, canGoPrevious, canGoNext } = useSegmentNavigation({
		editorView,
		segmentIndex,
		segments: streamReviewSegments,
	});

	const decoration = findModalDecoration(modalDecorationSet);
	if (!decoration || !modalDecorationSet) {
		return null;
	}

	return (
		<div ref={screenRef} css={wrapperCSS} data-testid="post-stream-review-session-screen">
			<ModalRegionErrorBoundary inContentComponent onCloseFallback={endExperience}>
				<ModalRegion
					lastTriggeredFrom={lastTriggeredFrom}
					key={modalMountedTimeStamp}
					decoration={decoration}
					editorView={editorView}
					editorRelativeWrapper={wrapperElement?.offsetParent}
					endExperience={endExperience}
					appearance={appearance}
					decorationSet={modalDecorationSet}
					isDirectionalPinningEnabled
				>
					<div css={toolbarCSS}>
						<AskRovo
							hasRovoPrompt={hasRovoPrompt}
							hasFallbackPrompt={hasFallbackPrompt}
							openInRovoChat={configItem?.openInRovoChat}
							openInRovoChatConfig={openInRovoChatConfig}
							cancel={cancel}
							PromptEditor={PromptEditor}
							textInput={textInput}
							onTextChange={onTextChange}
							submit={submit}
							appearance={appearance}
							isAIFC={isInDocumentStreamingEnabled}
							canUndo={canUndo}
							canRedo={canRedo}
							onUndo={onUndo}
							onRedo={onRedo}
							canViewChanges={canViewChanges}
							showDeletedChanges={showDeletedChanges}
							onViewChanges={onViewChanges}
							totalSegments={totalSegments}
							segmentIndex={segmentIndex}
							canGoPrevious={canGoPrevious}
							canGoNext={canGoNext}
							onPrevious={goToPrevious}
							onNext={goToNext}
							isStepThroughExpanded={isStepThroughExpanded}
							onToggleStepThrough={onToggleStepThrough}
						/>
					</div>
				</ModalRegion>
			</ModalRegionErrorBoundary>
		</div>
	);
}
